import React, { useRef, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const GradientShader = {
  uniforms: {
    uTime: { value: 0 },
    uMouse: { value: new THREE.Vector2(0, 0) },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uColor1: { value: new THREE.Color("#ff80bf") }, // Pink
    uColor2: { value: new THREE.Color("#9f7aea") }, // Purple
    uColor3: { value: new THREE.Color("#63b3ed") }, // Blue
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform float uTime;
    uniform vec2 uMouse;
    uniform vec2 uResolution;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    uniform vec3 uColor3;
    varying vec2 vUv;

    // Simplex 2D noise
    vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }

    float snoise(vec2 v){
      const vec4 C = vec4(0.211324865405187, 0.366025403784439,
               -0.577350269189626, 0.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy) );
      vec2 x0 = v -   i + dot(i, C.xx);
      vec2 i1;
      i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod(i, 289.0);
      vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
      + i.x + vec3(0.0, i1.x, 1.0 ));
      vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
      m = m*m ;
      m = m*m ;
      vec3 x = 2.0 * fract(p * C.www) - 1.0;
      vec3 h = abs(x) - 0.5;
      vec3 ox = floor(x + 0.5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
      vec3 g;
      g.x  = a0.x  * x0.x  + h.x  * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130.0 * dot(m, g);
    }

    void main() {
      vec2 st = vUv;
      
      // Adjust aspect ratio for distortion calculations
      float aspect = uResolution.x / uResolution.y;

      // Mouse influence
      vec2 mouse = uMouse * 0.5 + 0.5; // map -1..1 to 0..1
      
      // Pebble/Cloth Distortion Effect
      vec2 offset = st - mouse;
      offset.x *= aspect; // Correct for aspect ratio
      float dist = length(offset);
      float influence = smoothstep(0.8, 0.0, dist); // Radius of the pebble
      
      // Apply bulge distortion (pushing texture away from mouse)
      if (dist > 0.001) {
        st -= normalize(offset) * influence * 0.3 * vec2(1.0/aspect, 1.0);
      }
      
      // Create moving blobs
      float t = uTime * 0.2;
      
      // Blob 1 (Pink) - Center Left
      vec2 pos1 = vec2(0.0, 0.5) + vec2(0.1 * cos(t), 0.1 * sin(t * 1.2));
      float d1 = length(st - pos1 - (mouse - 0.5) * 0.1);
      float m1 = smoothstep(0.5, 0.0, d1);
      
      // Blob 2 (Purple) - Bottom Right
      vec2 pos2 = vec2(0.9, 0.2) + vec2(0.1 * sin(t * 0.8), 0.1 * cos(t));
      float d2 = length(st - pos2 + (mouse - 0.5) * 0.1);
      float m2 = smoothstep(0.5, 0.0, d2);
      
      // Blob 3 (Blue) - Bottom Left
      vec2 pos3 = vec2(0.1, 0.1) + vec2(0.15 * sin(t * 0.5), 0.15 * sin(t * 0.9));
      float d3 = length(st - pos3);
      float m3 = smoothstep(0.6, 0.0, d3);
      
      // Blob 4 (White->Color) - Top Left Corner
      vec2 pos4 = vec2(0.1, 0.9) + vec2(0.05 * sin(t * 0.7), 0.05 * cos(t * 0.6));
      float d4 = length(st - pos4);
      float m4 = smoothstep(0.5, 0.0, d4);
      
      // Blob 5 (White->Color) - Top Right Corner
      vec2 pos5 = vec2(0.9, 0.9) + vec2(0.05 * cos(t * 0.6), 0.05 * sin(t * 0.8));
      float d5 = length(st - pos5);
      float m5 = smoothstep(0.5, 0.0, d5);
      
      // Noise for texture
      float n = snoise(st * 3.0 + t * 0.5);
      
      // Dynamic colors based on mouse
      // Use uMouse (-1..1) for stronger color shift
      vec3 c1 = uColor1 + vec3(uMouse.x * 0.3, uMouse.y * 0.3, -uMouse.x * 0.3);
      vec3 c2 = uColor2 + vec3(uMouse.y * 0.3, -uMouse.x * 0.3, uMouse.y * 0.3);
      vec3 c3 = uColor3 + vec3(-uMouse.x * 0.3, uMouse.y * 0.3, uMouse.x * 0.3);
      
      // Top corner blobs: white by default, transition to color when mouse is near
      // Mouse in top-left: uMouse.x < 0 && uMouse.y > 0
      // Mouse in top-right: uMouse.x > 0 && uMouse.y > 0
      float topLeftInfluence = max(0.0, -uMouse.x) * max(0.0, uMouse.y);
      float topRightInfluence = max(0.0, uMouse.x) * max(0.0, uMouse.y);
      
      // Colors for top corners when activated
      vec3 c4 = mix(vec3(1.0), uColor1 * 1.1, topLeftInfluence * 1.5);  // White -> Pink
      vec3 c5 = mix(vec3(1.0), uColor3 * 1.1, topRightInfluence * 1.5); // White -> Blue

      // Mix colors
      vec3 color = mix(vec3(1.0), c1, m1);
      color = mix(color, c2, m2);
      color = mix(color, c3, m3);
      color = mix(color, c4, m4);
      color = mix(color, c5, m5);
      
      // Add some noise
      color += n * 0.03;
      
      // Soften/Blur effect is inherent in smoothstep, but we can make it more pastel
      // Fade to white/background at edges if needed, or keep full screen
      
      // Make it very light and subtle like the example
      color = mix(vec3(0.98, 0.98, 1.0), color, 0.5); // Base white/blueish tint

      gl_FragColor = vec4(color, 1.0);
    }
  `,
};

const GradientMesh = () => {
  const mesh = useRef();
  const { viewport, gl } = useThree();

  // Store mouse position in screen coords
  const screenMouse = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  // Store normalized mouse for the shader (viewport-relative, since canvas is now fixed)
  const normalizedMouse = useRef(new THREE.Vector2(0, 0));
  const frameErrored = useRef(false);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
      uColor1: { value: new THREE.Color("#FF80BF") }, // Pink
      uColor2: { value: new THREE.Color("#9F7AEA") }, // Purple
      uColor3: { value: new THREE.Color("#63B3ED") }, // Blue
    }),
    []
  );

  // Recalculate normalized mouse from screen coords
  const updateNormalizedMouse = React.useCallback(() => {
    if (gl.domElement) {
      const rect = gl.domElement.getBoundingClientRect();
      const x = screenMouse.current.x - rect.left;
      const y = screenMouse.current.y - rect.top;

      const nx = (x / rect.width) * 2 - 1;
      const ny = -((y / rect.height) * 2 - 1);

      // Clamp to keep influence visible
      normalizedMouse.current.set(
        Math.max(-0.95, Math.min(0.95, nx)),
        Math.max(-0.95, Math.min(0.95, ny))
      );
    }
  }, [gl]);

  React.useEffect(() => {
    const handleMouseMove = (event) => {
      screenMouse.current.x = event.clientX;
      screenMouse.current.y = event.clientY;
      updateNormalizedMouse();
    };

    const handleScroll = () => {
      // Recalc normalized mouse on scroll (canvas is fixed, so mouse relative to viewport doesn't change,
      // but this keeps things in sync if needed)
      updateNormalizedMouse();
    };

    const handleResize = () => {
      // Update resolution uniform on resize
      if (mesh.current?.material?.uniforms) {
        mesh.current.material.uniforms.uResolution.value.set(window.innerWidth, window.innerHeight);
      }
      updateNormalizedMouse();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateNormalizedMouse]);

  useFrame((state, delta) => {
    try {
      if (!mesh.current || !mesh.current.material || !mesh.current.material.uniforms) {
        return;
      }

      const u = mesh.current.material.uniforms;
      u.uTime.value += delta;

      // Smooth mouse movement toward the pre-calculated normalized position
      u.uMouse.value.lerp(normalizedMouse.current, 0.15);
    } catch (error) {
      if (!frameErrored.current) {
        console.error("GradientBackground useFrame error:", error);
        frameErrored.current = true;
      }
    }
  });

  return (
    <mesh ref={mesh} scale={[viewport.width, viewport.height, 1]}>
      <planeGeometry args={[1, 1]} />
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={GradientShader.vertexShader}
        fragmentShader={GradientShader.fragmentShader}
      />
    </mesh>
  );
};

const GradientBackground = () => {
  return (
    <div
      className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
    >
      <Canvas
        camera={{ position: [0, 0, 1] }}
        dpr={1}
        frameloop="always"
        style={{ pointerEvents: "none", width: "100%", height: "100%" }}
      >
        <GradientMesh />
      </Canvas>
      {/* Fade out at the bottom edge */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-[#FBFBFD] to-transparent pointer-events-none"
      />
    </div>
  );
};

export default GradientBackground;
